import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Robot, LockKey, Lightning } from "@phosphor-icons/react";

const BG = "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name || "Operator");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 grayscale" />
      <div className="absolute inset-0 bg-[#0A0B0E]/80 grid-texture" />

      <div className="relative w-full max-w-md border border-[#272A35] bg-[#111318] fade-up" data-testid="login-panel">
        <div className="flex items-center gap-3 border-b border-[#272A35] px-6 py-4">
          <Robot size={28} weight="bold" className="text-[#FF5A00]" />
          <div>
            <h1 className="font-head font-black text-xl tracking-tight leading-none">MECHALOG</h1>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#475569]">robot control terminal</p>
          </div>
        </div>

        <form onSubmit={submit} className="px-6 py-6 space-y-5">
          <div className="flex gap-1 border border-[#272A35]">
            <button
              type="button"
              data-testid="tab-login"
              onClick={() => setMode("login")}
              className={`flex-1 py-2 text-xs uppercase tracking-widest transition-colors ${mode === "login" ? "bg-[#FF5A00] text-[#0A0B0E] font-bold" : "text-[#94A3B8] hover:bg-[#1A1D24]"}`}
            >
              Sign In
            </button>
            <button
              type="button"
              data-testid="tab-register"
              onClick={() => setMode("register")}
              className={`flex-1 py-2 text-xs uppercase tracking-widest transition-colors ${mode === "register" ? "bg-[#FF5A00] text-[#0A0B0E] font-bold" : "text-[#94A3B8] hover:bg-[#1A1D24]"}`}
            >
              Register
            </button>
          </div>

          {mode === "register" && (
            <div>
              <label className="text-[10px] tracking-[0.15em] uppercase text-[#94A3B8] font-bold">Operator Name</label>
              <input
                data-testid="name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-transparent border-b-2 border-[#272A35] py-2 font-mono focus:outline-none focus:border-[#FF5A00] transition-colors"
                placeholder="e.g. Neo"
              />
            </div>
          )}

          <div>
            <label className="text-[10px] tracking-[0.15em] uppercase text-[#94A3B8] font-bold">Email</label>
            <input
              data-testid="email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b-2 border-[#272A35] py-2 font-mono focus:outline-none focus:border-[#FF5A00] transition-colors"
              placeholder="operator@mechalog.io"
            />
          </div>

          <div>
            <label className="text-[10px] tracking-[0.15em] uppercase text-[#94A3B8] font-bold flex items-center gap-1">
              <LockKey size={12} weight="bold" /> Password
            </label>
            <input
              data-testid="password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b-2 border-[#272A35] py-2 font-mono focus:outline-none focus:border-[#FF5A00] transition-colors"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p data-testid="auth-error" className="text-xs text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="auth-submit"
            disabled={loading}
            className="w-full bg-[#FF5A00] text-[#0A0B0E] font-bold uppercase tracking-widest py-3 border border-[#FF5A00] hover:bg-transparent hover:text-[#FF5A00] transition-colors active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Lightning size={16} weight="fill" />
            {loading ? "authenticating…" : mode === "login" ? "Access Terminal" : "Create Operator"}
          </button>

          <div className="flex items-center gap-3 py-1">
            <span className="h-px flex-1 bg-[#272A35]" />
            <span className="text-[10px] uppercase tracking-widest text-[#475569]">or</span>
            <span className="h-px flex-1 bg-[#272A35]" />
          </div>

          <button
            type="button"
            data-testid="google-signin-btn"
            onClick={loginWithGoogle}
            className="w-full flex items-center justify-center gap-3 border border-[#272A35] bg-[#0A0B0E] text-[#F8FAFC] py-3 uppercase tracking-widest text-xs font-bold hover:border-[#94A3B8] hover:bg-[#1A1D24] transition-colors active:scale-95"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.9 35.6 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <p className="text-[11px] text-[#475569] text-center">
            demo · admin@mechalog.io / robot123
          </p>
        </form>
      </div>
    </div>
  );
}
